import React, { useEffect, useRef } from "react";

const formatMoney = (value) =>
  `₹${(Number(value) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export function DenominationMismatchAlert({
  isOpen,
  onClose,
  onConfirm,
  onChangeDenomination,
  denomTotal,
  amountValue,
  returnAmount,
}) {
  const confirmButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => confirmButtonRef.current?.focus(), 0);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="bg-amber-50 px-6 py-4 border-b border-amber-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <span className="text-2xl">!</span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-amber-800">Denomination Mismatch</h3>
              <p className="text-sm text-amber-600">Please review the amounts</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-600">Denomination Total</span>
              <span className="font-semibold text-gray-900">{formatMoney(denomTotal)}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-600">MOI Amount</span>
              <span className="font-semibold text-gray-900">{formatMoney(amountValue)}</span>
            </div>
            <div className="flex justify-between items-center py-3 bg-amber-50 rounded-lg px-4">
              <span className="text-amber-800 font-medium">Return Amount</span>
              <span className="text-xl font-bold text-amber-700">{formatMoney(returnAmount)}</span>
            </div>
          </div>

          <p className="mt-4 text-sm text-gray-500 text-center">
            Please check the proper return amount before continuing.
          </p>
        </div>

        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onChangeDenomination || onClose}
            className="px-5 py-2.5 rounded-lg font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors"
          >
            Change Denomination
          </button>
          <button
            ref={confirmButtonRef}
            onClick={onConfirm || onClose}
            className="px-5 py-2.5 rounded-lg font-bold text-white bg-emerald-600 border border-emerald-700 hover:bg-emerald-700 focus:outline-none focus:ring-4 focus:ring-emerald-300 transition-colors"
          >
            Understood
          </button>
        </div>
      </div>
    </div>
  );
}

export function DenominationErrorBanner({ hasError, message }) {
  if (!hasError) return null;

  return (
    <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-xl">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-red-800">Amount Exceeds Denomination Total</p>
          <p className="text-sm text-red-600 mt-1">{message || "The entered amount cannot be more than the denomination total."}</p>
        </div>
      </div>
    </div>
  );
}
